import type { ImgHTMLAttributes, ReactNode } from "react";
import markDarkUrl from "../../../assets/logo/mfa-mark-dark.svg";
import markUrl from "../../../assets/logo/mfa-mark.svg";
import wordmarkDarkUrl from "../../../assets/logo/mfa-wordmark-dark.svg";
import wordmarkUrl from "../../../assets/logo/mfa-wordmark.svg";
import { cx } from "../../../utils/cx";

type LogoProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "alt"> & {
    /** Accessible name; defaults to the brand name. */
    alt?: string;
    /** Force a colour variant (e.g. `onDark` on a brand-gradient panel). Defaults to following the theme. */
    variant?: "auto" | "onLight" | "onDark";
};

/** Theme-aware pair of vector images (light asset, dark asset). */
const ThemedImg = ({ light, dark, className, alt, variant = "auto", ...props }: LogoProps & { light: string; dark: string }) => {
    if (variant !== "auto") {
        return <img src={variant === "onDark" ? dark : light} alt={alt} className={cx("w-auto max-w-none select-none", className)} draggable={false} {...props} />;
    }
    return (
        <>
            <img src={light} alt={alt} className={cx("w-auto max-w-none select-none dark:hidden", className)} draggable={false} {...props} />
            <img src={dark} alt={alt} className={cx("hidden w-auto max-w-none select-none dark:block", className)} draggable={false} {...props} />
        </>
    );
};

/** Persian logotype «میزبان‌فا» (vector, traced from the official logo). */
export const MizbanfaLogo = ({ className, alt = "میزبان‌فا", ...props }: LogoProps) => (
    <ThemedImg light={wordmarkUrl} dark={wordmarkDarkUrl} alt={alt} className={cx("h-8", className)} {...props} />
);

/** Square brand mark only (blue M with green bars) — collapsed sidebar, favicon, avatars. */
export const MizbanfaLogoMinimal = ({ className, alt = "میزبان‌فا", ...props }: LogoProps) => (
    <ThemedImg light={markUrl} dark={markDarkUrl} alt={alt} className={cx("h-8", className)} {...props} />
);

/**
 * Product lockup: «میزبان‌فا | بازاریابی» (official logotype + panel name). The caption is the panel name
 * (partner: «بازاریابی», admin: «مدیریت بازاریابی»).
 */
export const MizbanfaLockup = ({
    caption = "بازاریابی",
    size = "md",
    variant = "auto",
    className,
}: {
    caption?: ReactNode;
    size?: "sm" | "md" | "lg";
    variant?: LogoProps["variant"];
    className?: string;
}) => {
    const s = {
        sm: { word: "h-6", caption: "text-xs", gap: "gap-2", divider: "h-5" },
        md: { word: "h-7", caption: "text-sm", gap: "gap-2.5", divider: "h-6" },
        lg: { word: "h-9", caption: "text-md", gap: "gap-3", divider: "h-7" },
    }[size];

    return (
        <div className={cx("flex min-w-0 items-center", s.gap, className)} role="img" aria-label={`میزبان‌فا، ${typeof caption === "string" ? caption : "بازاریابی"}`}>
            <MizbanfaLogo variant={variant} alt="" aria-hidden="true" className={cx("shrink-0", s.word)} />
            {caption && (
                <>
                    <span aria-hidden="true" className={cx("w-px shrink-0", s.divider, variant === "onDark" ? "bg-white/30" : "bg-border-primary")} />
                    <span
                        aria-hidden="true"
                        className={cx("line-clamp-2 leading-tight font-semibold", s.caption, variant === "onDark" ? "text-white/90" : "text-secondary")}
                    >
                        {caption}
                    </span>
                </>
            )}
        </div>
    );
};
